/**
 * Budget administration: the year, the tiers, and the ledger.
 *
 * All three are things that used to need SQL, and each carries a way to make the app unable to
 * explain itself — so each one states its guard on screen rather than only in the refusal:
 *
 *   - **Closing a year forfeits its balance** (R-06), so it is refused while anything is in flight.
 *   - **Re-tiering a year with spending against it** would leave the ledger computed from rungs
 *     that no longer exist. An adjustment is how a figure changes after the fact.
 *   - **An adjustment's sign reads backwards**, so this form asks for a direction instead.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useSession, sgtYear } from '@/hooks/session';
import { ErrorNote, Money, PageHeader } from '@/components/bits';
import {
  Alert,
  Badge,
  Button,
  Card,
  Dialog,
  Field,
  Input,
  Select,
  Table,
  Td,
  Th,
} from '@/components/ui';
import { formatCentsPlain, parseAmountToCents } from '@/lib/money';
import { formatDate, formatInstant } from '@/lib/dates';
import type { EntitlementTier } from '@/lib/types';

export function HrBudget() {
  const session = useSession();
  const owner = session.me.user.role === 'superadmin';

  return (
    <>
      <PageHeader
        title="Budget administration"
        subtitle="The year, the entitlement ladder, and corrections to the ledger."
      />

      {owner ? null : (
        <Alert tone="neutral" title="Some of this is the owner’s alone" className="mb-6">
          Opening and closing a year and editing the entitlement rates need <code>superadmin</code>.
          Ledger adjustments are open to any admin, with a reason.
        </Alert>
      )}

      <div className="grid gap-6">
        <Years canEdit={owner} />
        <Rates canEdit={owner} />
        <Adjustments />
      </div>
    </>
  );
}

// ------------------------------------------------------------------------------ years -----

function Years({ canEdit }: { canEdit: boolean }) {
  const cache = useQueryClient();
  const [opening, setOpening] = useState(false);
  const [year, setYear] = useState(() => String(sgtYear() + 1));
  const [closing, setClosing] = useState<number | null>(null);

  const years = useQuery({ queryKey: ['budget-years'], queryFn: api.budgetYears });

  const invalidate = () => cache.invalidateQueries({ queryKey: ['budget-years'] });

  const open = useMutation({
    mutationFn: () => api.openBudgetYear(Number(year)),
    onSuccess: async () => {
      setOpening(false);
      await invalidate();
    },
  });

  const close = useMutation({
    mutationFn: (target: number) => api.closeBudgetYear(target),
    onSuccess: async () => {
      setClosing(null);
      await invalidate();
    },
  });

  const priorPeriod = useMutation({
    mutationFn: (input: { year: number; loaded: boolean }) =>
      api.setPriorPeriodLoaded(input.year, input.loaded),
    onSuccess: invalidate,
  });

  return (
    <Card
      title="Budget years"
      action={
        canEdit ? (
          <Button size="sm" variant="primary" onClick={() => setOpening(true)}>
            Open a year
          </Button>
        ) : null
      }
    >
      {years.isPending ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Year</Th>
              <Th>Opens</Th>
              <Th>Closes</Th>
              <Th>Claim grace (R-24)</Th>
              <Th>Status</Th>
              <Th>Prior history</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {(years.data?.years ?? []).map((entry) => (
              <tr key={entry.year}>
                <Td className="tabular">{entry.year}</Td>
                <Td className="text-xs whitespace-nowrap">{formatDate(entry.opens_at)}</Td>
                <Td className="text-xs whitespace-nowrap">{formatDate(entry.closes_at)}</Td>
                <Td className="text-xs whitespace-nowrap">
                  {formatDate(entry.claim_grace_deadline)}
                </Td>
                <Td>
                  <Badge tone={entry.status === 'open' ? 'good' : 'neutral'}>{entry.status}</Badge>
                </Td>
                <Td>
                  {entry.prior_period_loaded_at ? (
                    <>
                      <Badge tone="good">loaded</Badge>
                      <span className="text-muted-foreground block text-xs">
                        {formatDate(entry.prior_period_loaded_at.slice(0, 10))}
                      </span>
                    </>
                  ) : (
                    <>
                      <Badge tone="warn">not loaded</Badge>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="mt-1"
                        busy={priorPeriod.isPending && priorPeriod.variables?.year === entry.year}
                        onClick={() => priorPeriod.mutate({ year: entry.year, loaded: true })}
                      >
                        Mark loaded
                      </Button>
                    </>
                  )}
                </Td>
                <Td>
                  {canEdit && entry.status === 'open' ? (
                    <Button size="sm" onClick={() => setClosing(entry.year)}>
                      Close
                    </Button>
                  ) : null}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <p className="text-muted-foreground mt-3 text-xs">
        <strong>Prior history</strong> is D-23: HR035 took effect on 1 January 2026 and this app
        arrived in August, so events run under the email process before it existed are not in the
        ledger. Until a year is marked loaded, every screen warns that its figures may be too high —
        because the entitlement is shown in full while part of it has already been spent. Mark it
        loaded once the backfill (retrospective records, or <code>adjustment</code> rows) is in.
      </p>
      {priorPeriod.error ? <ErrorNote error={priorPeriod.error} /> : null}

      <p className="text-muted-foreground mt-3 text-xs">
        January has two years live at once: last year&rsquo;s events can still be claimed up to the
        grace deadline while this year&rsquo;s proposals are already being raised. That is what the
        year selector in the header is for.
      </p>

      <Dialog
        open={opening}
        onClose={() => setOpening(false)}
        title="Open a budget year"
        footer={
          <>
            <Button onClick={() => setOpening(false)}>Cancel</Button>
            <Button variant="primary" busy={open.isPending} onClick={() => open.mutate()}>
              Open {year}
            </Button>
          </>
        }
      >
        <p className="text-muted-foreground">
          Nothing can be spent against a year that does not exist — the calculator refuses loudly
          rather than assuming a default. So this is the first thing done each January.
        </p>
        <Field label="Year" required hint="The claim grace deadline defaults to 15 January after.">
          {(props) => (
            <Input
              {...props}
              inputMode="numeric"
              value={year}
              onChange={(event) => setYear(event.target.value)}
            />
          )}
        </Field>
        {open.error ? <ErrorNote error={open.error} /> : null}
      </Dialog>

      <Dialog
        open={closing !== null}
        onClose={() => setClosing(null)}
        title={`Close ${closing ?? ''}`}
        footer={
          <>
            <Button onClick={() => setClosing(null)}>Cancel</Button>
            <Button
              variant="destructive"
              busy={close.isPending}
              onClick={() => close.mutate(closing as number)}
            >
              Close the year
            </Button>
          </>
        }
      >
        <Alert tone="warn" title="This forfeits whatever is unspent">
          Closing a year is what makes R-06 real: the balance is forfeited, not carried into the
          next year. It is refused while any proposal or claim is still in flight, so nothing gets
          stranded holding budget that no longer exists.
        </Alert>
        {close.error ? <ErrorNote error={close.error} /> : null}
      </Dialog>
    </Card>
  );
}

// ------------------------------------------------------------------------------ rates -----

interface TierDraft {
  min_months: string;
  staff: string;
  worker: string;
}

function Rates({ canEdit }: { canEdit: boolean }) {
  const session = useSession();
  const cache = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<TierDraft[]>([]);

  const rates = useQuery({
    queryKey: ['budget-rates', session.year],
    queryFn: () => api.budgetRates(session.year),
  });

  useEffect(() => {
    if (!rates.data) return;
    setDraft(toDraft(rates.data.tiers));
  }, [rates.data]);

  const save = useMutation({
    mutationFn: () =>
      api.setBudgetRates(
        session.year,
        draft.map((tier, index) => ({
          min_months: Number(tier.min_months),
          // The ladder is contiguous: each tier runs up to where the next one starts.
          max_months: index + 1 < draft.length ? Number(draft[index + 1]?.min_months) : null,
          staff_cents: parseAmountToCents(tier.staff) ?? 0,
          worker_cents: parseAmountToCents(tier.worker) ?? 0,
        })),
      ),
    onSuccess: async () => {
      setEditing(false);
      await cache.invalidateQueries({ queryKey: ['budget-rates'] });
      await cache.invalidateQueries({ queryKey: ['budget-departments'] });
    },
  });

  const valid = draft.every(
    (tier) =>
      /^\d+$/.test(tier.min_months.trim()) &&
      parseAmountToCents(tier.staff) !== null &&
      parseAmountToCents(tier.worker) !== null,
  );

  return (
    <Card
      title={`Entitlement rates — ${session.year}`}
      action={
        canEdit && rates.data ? (
          editing ? (
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => {
                  setDraft(toDraft(rates.data?.tiers ?? []));
                  setEditing(false);
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                variant="primary"
                busy={save.isPending}
                disabled={!valid || draft.length === 0}
                onClick={() => save.mutate()}
              >
                Save the ladder
              </Button>
            </div>
          ) : (
            <Button size="sm" onClick={() => setEditing(true)}>
              Edit
            </Button>
          )
        ) : null
      }
    >
      {rates.isPending ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : rates.error ? (
        <Alert tone="warn" title={`No rates are configured for ${session.year}`}>
          Nobody&rsquo;s entitlement can be computed until they are. Open the year first, then set
          the ladder.
        </Alert>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Service at year end</Th>
              <Th numeric>Staff</Th>
              <Th numeric>Worker</Th>
              {editing ? <Th /> : null}
            </tr>
          </thead>
          <tbody>
            {(editing ? draft : toDraft(rates.data?.tiers ?? [])).map((tier, index) => (
              <tr key={index}>
                <Td>
                  {editing ? (
                    <div className="flex items-center gap-2">
                      <Input
                        aria-label={`Tier ${index + 1} minimum months`}
                        className="w-20"
                        inputMode="numeric"
                        value={tier.min_months}
                        onChange={(event) =>
                          setDraft(update(draft, index, { min_months: event.target.value }))
                        }
                      />
                      <span className="text-muted-foreground text-xs">months or more</span>
                    </div>
                  ) : (
                    `${tier.min_months} months or more`
                  )}
                </Td>
                <Td numeric>
                  {editing ? (
                    <Input
                      aria-label={`Tier ${index + 1} staff amount`}
                      className="w-28 text-right"
                      inputMode="decimal"
                      value={tier.staff}
                      onChange={(event) =>
                        setDraft(update(draft, index, { staff: event.target.value }))
                      }
                    />
                  ) : (
                    <Money cents={parseAmountToCents(tier.staff) ?? 0} />
                  )}
                </Td>
                <Td numeric>
                  {editing ? (
                    <Input
                      aria-label={`Tier ${index + 1} worker amount`}
                      className="w-28 text-right"
                      inputMode="decimal"
                      value={tier.worker}
                      onChange={(event) =>
                        setDraft(update(draft, index, { worker: event.target.value }))
                      }
                    />
                  ) : (
                    <Money cents={parseAmountToCents(tier.worker) ?? 0} />
                  )}
                </Td>
                {editing ? (
                  <Td>
                    <Button
                      size="sm"
                      onClick={() => setDraft(draft.filter((_, at) => at !== index))}
                    >
                      Remove
                    </Button>
                  </Td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {editing ? (
        <div className="mt-3 flex items-center gap-3">
          <Button
            size="sm"
            onClick={() => setDraft([...draft, { min_months: '', staff: '', worker: '' }])}
          >
            Add a tier
          </Button>
          <span className="text-muted-foreground text-xs">
            Each tier runs up to where the next one starts; the last one has no ceiling.
          </span>
        </div>
      ) : null}

      {save.error ? (
        <div className="mt-3">
          <ErrorNote error={save.error} />
        </div>
      ) : null}

      <p className="text-muted-foreground mt-3 text-xs">
        Service is measured at the <strong>close</strong> of the budget year, not on the event date
        (D-53) — which is why the year&rsquo;s figure is stable from January. Under three months
        brings nothing (R-04). Editing is refused once the year has entitlement movements against
        it: the ledger has to keep explaining itself.
      </p>
    </Card>
  );
}

const toDraft = (tiers: readonly EntitlementTier[]): TierDraft[] =>
  tiers.map((tier) => ({
    min_months: String(tier.min_months),
    staff: formatCentsPlain(tier.staff_cents),
    worker: formatCentsPlain(tier.worker_cents),
  }));

const update = (draft: TierDraft[], index: number, patch: Partial<TierDraft>): TierDraft[] =>
  draft.map((tier, at) => (at === index ? { ...tier, ...patch } : tier));

// ------------------------------------------------------------------------ adjustments -----

function Adjustments() {
  const session = useSession();
  const cache = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [employee, setEmployee] = useState<{ id: number; name: string } | null>(null);
  const [direction, setDirection] = useState<'spent' | 'restore'>('spent');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  const results = useQuery({
    queryKey: ['employees', 'adjust', search],
    queryFn: () => api.employees({ search: search.trim(), limit: 10 }),
    enabled: search.trim().length >= 2,
  });

  const ledger = useQuery({
    queryKey: ['ledger', employee?.id, session.year],
    queryFn: () => api.ledger(employee?.id as number, session.year),
    enabled: employee !== null,
  });

  const cents = parseAmountToCents(amount);

  const post = useMutation({
    mutationFn: () =>
      api.postAdjustment({
        budget_year: session.year,
        employee_id: employee?.id as number,
        // The sign is derived from the direction, never typed: a positive adjustment *reduces*
        // what is available, which is the opposite of how anybody reads "amount".
        amount_cents: direction === 'spent' ? (cents as number) : -(cents as number),
        reason: reason.trim(),
      }),
    onSuccess: async () => {
      setOpen(false);
      setAmount('');
      setReason('');
      await cache.invalidateQueries({ queryKey: ['ledger'] });
      await cache.invalidateQueries({ queryKey: ['budget-departments'] });
      await cache.invalidateQueries({ queryKey: ['calculate-roster'] });
      await cache.invalidateQueries({ queryKey: ['calculate-selection'] });
    },
  });

  return (
    <Card
      title="Ledger adjustments"
      action={
        <Button
          size="sm"
          variant="primary"
          disabled={employee === null}
          onClick={() => setOpen(true)}
        >
          Post an adjustment
        </Button>
      }
    >
      <div className="grid gap-4">
        <p className="text-muted-foreground text-sm">
          The only way to write history the workflows did not produce — which makes this the vehicle
          for the deferred 2026 backfill (D-23) as well as for correcting a mistake. The ledger is
          append-only, so a correction is another row and both stay visible.
        </p>

        <Field label="Whose ledger" hint="Search by name or employee number.">
          {(props) => (
            <Input
              {...props}
              value={search}
              placeholder="At least two characters"
              onChange={(event) => setSearch(event.target.value)}
            />
          )}
        </Field>

        {search.trim().length >= 2 && !results.isPending ? (
          <ul className="grid gap-1">
            {(results.data?.employees ?? []).map((row) => (
              <li key={row.id} className="flex items-center justify-between gap-2 text-sm">
                <span>
                  {row.full_name}{' '}
                  <span className="text-muted-foreground text-xs">
                    {row.employee_no} · {row.department_name ?? 'unassigned'}
                  </span>
                </span>
                <Button size="sm" onClick={() => setEmployee({ id: row.id, name: row.full_name })}>
                  Open ledger
                </Button>
              </li>
            ))}
          </ul>
        ) : null}

        {employee ? (
          <div className="border-border border-t pt-4">
            <h3 className="text-sm font-medium">
              {employee.name} — {session.year}
            </h3>
            {ledger.isPending ? (
              <p className="text-muted-foreground mt-2 text-sm">Loading…</p>
            ) : (ledger.data?.entries ?? []).length === 0 ? (
              <p className="text-muted-foreground mt-2 text-sm">
                Nothing has moved on this ledger yet.
              </p>
            ) : (
              <Table className="mt-2">
                <thead>
                  <tr>
                    <Th>When</Th>
                    <Th>Kind</Th>
                    <Th numeric>Amount</Th>
                    <Th>Cites</Th>
                    <Th>Note</Th>
                  </tr>
                </thead>
                <tbody>
                  {(ledger.data?.entries ?? []).map((entry) => (
                    <tr key={entry.id}>
                      <Td className="text-xs whitespace-nowrap">
                        {formatInstant(entry.created_at)}
                      </Td>
                      <Td>
                        <Badge
                          tone={
                            entry.entry_type === 'usage'
                              ? 'good'
                              : entry.entry_type === 'adjustment'
                                ? 'warn'
                                : 'neutral'
                          }
                        >
                          {entry.entry_type}
                        </Badge>
                      </Td>
                      <Td numeric>
                        <Money cents={entry.amount_cents} />
                      </Td>
                      <Td className="text-xs">
                        {entry.proposal_id ? `proposal #${entry.proposal_id}` : ''}
                        {entry.claim_id ? ` claim #${entry.claim_id}` : ''}
                        {entry.rule_id ? ` ${entry.rule_id}` : ''}
                      </Td>
                      <Td className="text-xs">{entry.note ?? '—'}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        ) : null}
      </div>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title={`Adjust ${employee?.name ?? ''}`}
        footer={
          <>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              variant="primary"
              busy={post.isPending}
              disabled={cents === null || cents === 0 || reason.trim().length < 3}
              onClick={() => post.mutate()}
            >
              Post it
            </Button>
          </>
        }
      >
        {/*
          A direction, not a sign. The ledger folds a signed adjustment into `used_cents`, so a
          positive number *reduces* what somebody has available — the opposite of how "amount"
          reads. Asking the question in words removes the trap entirely.
        */}
        <Field label="What is this recording" required>
          {(props) => (
            <Select
              {...props}
              value={direction}
              onChange={(event) => setDirection(event.target.value as 'spent' | 'restore')}
            >
              <option value="spent">Money already spent — reduces what they have available</option>
              <option value="restore">
                Giving budget back — increases what they have available
              </option>
            </Select>
          )}
        </Field>

        <Field
          label="Amount"
          required
          error={
            amount.trim() !== '' && cents === null ? 'Enter an amount like 25 or 25.50.' : null
          }
        >
          {(props) => (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">S$</span>
              <Input
                {...props}
                inputMode="decimal"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
            </div>
          )}
        </Field>

        <Field label="Reason" required hint="Recorded on the ledger row and in the audit trail.">
          {(props) => (
            <Input
              {...props}
              value={reason}
              placeholder="Spent under the email process before go-live (D-23)."
              onChange={(event) => setReason(event.target.value)}
            />
          )}
        </Field>

        {cents !== null && cents > 0 ? (
          <Alert tone={direction === 'spent' ? 'warn' : 'good'}>
            {employee?.name}&rsquo;s available entitlement will{' '}
            <strong>{direction === 'spent' ? 'fall' : 'rise'}</strong> by <Money cents={cents} />.
          </Alert>
        ) : null}

        {post.error ? <ErrorNote error={post.error} /> : null}
      </Dialog>
    </Card>
  );
}
